<template>
  <BaseModal :active="modelValue" @update:active="close" @closed="onClosed">
    <template #title>Q-Tool</template>

    <template #default>
      <div
        v-if="mode.type === 'connecting'"
        class="flex flex-col items-center justify-center gap-4 py-12"
      >
        <em class="mdi mdi-loading animate-spin text-4xl text-blue-500" />
        <div class="text-center">
          <p class="font-medium">Verbinde mit {{ connectedQToolName }}...</p>
        </div>
      </div>

      <QToolDiscoveryScreen
        v-else-if="!isConnected"
        :qtools="discoveredQTools"
        :is-searching="mode.type === 'idle' || mode.type === 'qtoolDiscovery'"
        @select="onQToolSelected"
      />

      <QToolFirmwareUpdateScreen
        v-else-if="firmwareBlocking"
        :state="firmwareState"
        :dismissing="firmwareDismissing"
        @start="startFirmwareUpdate"
        @dismiss="dismissFirmwareUpdate"
      />

      <QToolReadoutScreen
        v-else
        :filter-devices="filterDevices"
        :readout-devices="readoutDevices"
        :matched-count="matchedCount"
        :filter-count="filterItems.length"
        :total-count="readoutDevices.length"
        :readout-state="readoutState"
        @re-pair="onRePair"
      />
    </template>

    <template v-if="isConnected && !firmwareBlocking" #buttons>
      <button
        class="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-dark-3"
        @click="onRePair"
      >
        Anderes Q-Tool wählen
      </button>
      <button
        class="rounded-lg bg-blue-500 px-4 py-2 text-sm text-white"
        data-testid="qToolDoneButton"
        @click="onDone"
      >
        Fertig
      </button>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, watch, toRef } from 'vue';
import BaseModal from '@/components/UI/Modal/BaseModal.vue';
import QToolDiscoveryScreen from '@/components/QTool/QToolDiscoveryScreen.vue';
import QToolReadoutScreen from '@/components/QTool/QToolReadoutScreen.vue';
import QToolFirmwareUpdateScreen from '@/components/QTool/QToolFirmwareUpdateScreen.vue';
import { useQTool } from '@/composables/useQTool';
import { useQToolFirmwareUpdate } from '@/composables/useQToolFirmwareUpdate';
import type { FilterListItem } from '@/native/plugins/WebEvents';

const props = defineProps<{
  modelValue: boolean;
  filterList: FilterListItem[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  connectivityChange: [isConnected: boolean];
  completed: [];
}>();

const filterItems = toRef(props, 'filterList');

const {
  discoveredQTools,
  mode,
  connectedQToolName,
  readoutState,
  readoutDevices,
  filterDevices,
  matchedCount,
  isConnected,
  startDiscovery,
  connectToQTool,
  stop,
  disconnect,
} = useQTool(filterItems);

const {
  state: firmwareState,
  isBlocking: firmwareBlocking,
  dismissing: firmwareDismissing,
  startUpdate: startFirmwareUpdate,
  dismiss: dismissFirmwareUpdate,
} = useQToolFirmwareUpdate();

watch(isConnected, (connected) => {
  emit('connectivityChange', connected);
});

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      startDiscovery();
    }
  }
);

function onQToolSelected(address: string) {
  connectToQTool(address);
}

function onRePair() {
  disconnect();
  startDiscovery();
}

function close() {
  emit('update:modelValue', false);
}

function onDone() {
  emit('completed');
  emit('update:modelValue', false);
}

async function onClosed() {
  await stop();
}
</script>
